import {
	type OptionModelDto,
	type StandardClassificationId,
	type StandardInstrumentsClassificationDto,
	type UserInstrumentClassificationDto,
	Color,
	DataVisualization,
	InstrumentFieldType,
	TagType,
} from "@eu.mdotm.api/sphere";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Banner,
	ButtonGroupRadio,
	CircularProgressBar,
	Icon,
	Row,
	Text,
	TooltipContent,
} from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { noop, typedObjectEntries, typedObjectValues } from "@mdotm/shadcn/legacy/utils";
import { type SelectOption, type SelectOptions, FormField, Label } from "@mdotm/shadcn/mdotm/components";
import type { MaybePromise } from "@mdotm/utils/promise";
import { ForEach } from "@mdotm/utils/react-extra";
import { useQuery } from "@tanstack/react-query";
import * as Immutable from "immutable";
import { useMemo } from "react";
import { type Control, useFieldArray, useForm } from "react-hook-form";
import { match } from "ts-pattern";
import * as z from "zod";
import { FormSidebarContent, spawnSidePanel } from "$root/components/common/SidePanel";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { useDebouncedNameUniquenessChecker } from "$root/functional-areas/named-entities/uniqueness";
import { useUserValue } from "$root/functional-areas/user";
import { userRepository } from "$root/functional-areas/user/repository";
import { FormController } from "$root/third-party-integrations/react-hook-form";
import { useMappedQueries } from "$root/third-party-integrations/react-query/hooks";
import { typedQueryClient } from "$root/third-party-integrations/react-query/typedQueryClient";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { type TagOption, TagOptionsEditor } from "$root/ui-lib/form/TagOptionsEditor";
import { getThemeCssVars, lazy, qualifier } from "$root/utils";
import { InfoTooltip } from "$root/widgets-architecture/layout/WidgetsMapper/InfoTooltip";
import { instrumentsRepository } from "../repository";
import { spawnInstrumentCustomisationDeleteDialog } from "./delete-dialog";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { DUAL_SOURCE_COVERAGE_BANNER, dualSourceCoverage, presetOptionFromClassification } from "./preset-availability";

// NOTE: themecssvars which need to be resolved lazily
export const colorMap: Record<
	Color,
	{
		badgeColor: () => string;
		chartColor: () => string;
		label: string;
		key: Color;
	}
> = {
	DEEP_RED: {
		badgeColor: lazy(() => getThemeCssVars(themeCSSVars.palette_R100)),
		chartColor: lazy(() => getThemeCssVars(themeCSSVars.palette_R300)),
		label: "Deep red",
		key: "DEEP_RED",
	},
	BRIGHT_RED: {
		badgeColor: lazy(() => getThemeCssVars(themeCSSVars.palette_D100)),
		chartColor: lazy(() => getThemeCssVars(themeCSSVars.palette_D300)),
		label: "Bright red",
		key: "BRIGHT_RED",
	},
	ORANGE: {
		badgeColor: lazy(() => getThemeCssVars(themeCSSVars.palette_W100)),
		chartColor: lazy(() => getThemeCssVars(themeCSSVars.palette_W300)),
		label: "Orange",
		key: "ORANGE",
	},
	YELLOW: {
		badgeColor: lazy(() => getThemeCssVars(themeCSSVars.palette_A100)),
		chartColor: lazy(() => getThemeCssVars(themeCSSVars.palette_A300)),
		label: "Yellow",
		key: "YELLOW",
	},
	LIGHT_BLUE: {
		badgeColor: lazy(() => getThemeCssVars(themeCSSVars.palette_S100)),
		chartColor: lazy(() => getThemeCssVars(themeCSSVars.palette_S300)),
		label: "Light blue",
		key: "LIGHT_BLUE",
	},
	BLUEBERRY: {
		badgeColor: lazy(() => getThemeCssVars(themeCSSVars.palette_B100)),
		chartColor: lazy(() => getThemeCssVars(themeCSSVars.palette_B300)),
		label: "Blueberry",
		key: "BLUEBERRY",
	},
	VIOLET: {
		badgeColor: lazy(() => getThemeCssVars(themeCSSVars.palette_V100)),
		chartColor: lazy(() => getThemeCssVars(themeCSSVars.palette_V300)),
		label: "Violet",
		key: "VIOLET",
	},
	GRAY: {
		badgeColor: lazy(() => getThemeCssVars(themeCSSVars.palette_N100)),
		chartColor: lazy(() => getThemeCssVars(themeCSSVars.palette_N300)),
		label: "Gray",
		key: "GRAY",
	},
	GREEN: {
		badgeColor: lazy(() => getThemeCssVars(themeCSSVars.palette_P100)),
		chartColor: lazy(() => getThemeCssVars(themeCSSVars.palette_P300)),
		label: "Green",
		key: "GREEN",
	},
};

export const colors = typedObjectValues(colorMap);

//UserInstrumentClassificationDto : replace with api def
type EditClassificationOnlyKeys = "classificationUuid" | "used" | "userId";

export type CreateClassification = Omit<UserInstrumentClassificationDto, EditClassificationOnlyKeys>;

type InstrumentCustomizationSidePanelProps = {
	onCancel(): void;
	setCanDismiss(v: boolean): void;
} & (
	| {
			mode: "creation";
			onSubmitAsync(classification: CreateClassification): MaybePromise<void>;
	  }
	| {
			mode: "edit";
			classification: UserInstrumentClassificationDto;
			onSubmitAsync(classification: UserInstrumentClassificationDto): MaybePromise<void>;
	  }
);

const allowedColumnName = /^[a-zA-Z0-9[\](){}_ -]{1,30}$/;

function castedZodClassification(checkIfNameIsAvailable: (name: string) => Promise<boolean>) {
	return {
		classificationUuid: z.string(),
		name: z
			.string()
			.regex(allowedColumnName, { message: "Please provide a valid name" })
			.refine((name) => checkIfNameIsAvailable(name), {
				message: "A column with this name already exists",
			}),
		fieldType: z.nativeEnum(InstrumentFieldType, {
			required_error: "Please select a valid column type",
		}),
		tagType: z.nativeEnum(TagType).optional(),
		standardClassificationId: z.string().optional().nullable() /** z.nativeEnum(StandardClassificationId) */,
		dataVisualization: z.nativeEnum(DataVisualization).optional(),
		modificationTime: z.string().optional(),
		options: z
			.array(
				z.object({
					color: z.nativeEnum(Color).optional(),
					option: z.string(),
				} satisfies Record<keyof OptionModelDto, unknown>),
			)
			.optional(),
	} satisfies Record<keyof Omit<UserInstrumentClassificationDto, "used" | "userId">, unknown>;
}

/** @internal — exported for tests */
export const creationResolver = (checkIfNameIsAvailable: (name: string) => Promise<boolean>) => {
	const { classificationUuid: _classificationUuid, ...restZodClassification } =
		castedZodClassification(checkIfNameIsAvailable);
	return z.object({ ...restZodClassification } satisfies Record<keyof CreateClassification, unknown>).refine(
		(form) => {
			if (form.fieldType === "TAG") {
				return form.tagType != null;
			}
			return true;
		},
		{
			message: "Please select a valid tag type",
			path: ["tagType"],
		},
	);
};
/** @internal — exported for tests */
export const editResolver = (checkIfNameIsAvailable: (name: string) => Promise<boolean>) => {
	const { classificationUuid: _classificationUuid, ...restZodClassification } =
		castedZodClassification(checkIfNameIsAvailable);
	return z.object({ ...restZodClassification } satisfies Record<keyof CreateClassification, unknown>);
};

type ClassificationAllowedAction = Record<
	| "canEditType"
	| "canEditTagType"
	| "canEditName"
	| "canEditVisualization"
	| "canEditOptionName"
	| "canEditOptionOrder"
	| "canEditOptionColor"
	| "canAddOption"
	| "canRemoveOption",
	boolean
>;

/** @internal — exported for tests */
export function classificationAllowedAction(
	mode: InstrumentCustomizationSidePanelProps["mode"],
	standardColumnId?: string,
): ClassificationAllowedAction {
	if (mode === "edit") {
		return {
			canEditType: false,
			canEditTagType: false,
			canEditName: true,
			canEditVisualization: true,
			canEditOptionName: true,
			canEditOptionOrder: true,
			canEditOptionColor: true,
			canAddOption: true,
			canRemoveOption: true,
		};
	}

	if (mode === "creation" && standardColumnId) {
		return {
			canEditType: false,
			canEditTagType: true,
			canEditName: true,
			canEditVisualization: true,
			canEditOptionName: false,
			canEditOptionOrder: true,
			canEditOptionColor: true,
			canAddOption: true,
			canRemoveOption: false,
		};
	}

	return {
		canEditType: true,
		canEditTagType: true,
		canEditName: true,
		canEditVisualization: true,
		canEditOptionName: true,
		canEditOptionOrder: true,
		canEditOptionColor: true,
		canAddOption: true,
		canRemoveOption: true,
	};
}

type EditableInstrumentClassificationOption = OptionModelDto & {
	canEdit?: boolean;
};
type InstrumentClassificationForm = Omit<UserInstrumentClassificationDto, "options"> & {
	options?: Array<EditableInstrumentClassificationOption>;
};

function InstrumentCustomizationSidePanel(props: InstrumentCustomizationSidePanelProps): React.JSX.Element {
	const { onCancel, onSubmitAsync, mode } = props;

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const defaultValues = useMemo<InstrumentClassificationForm>(() => {
		if (mode === "creation") {
			return { name: "" };
		}
		const { options, ...rest } = props.classification;
		const remappedOptions = options?.map(
			(x): EditableInstrumentClassificationOption => ({
				...x,
				canEdit: false,
				color: x.color ?? "GRAY",
			}),
		);
		return { ...rest, options: remappedOptions };
	}, [mode]);

	const { checkIfNameIsAvailable, checkingNameUniqueness } = useDebouncedNameUniquenessChecker({
		isNameAvailableApi: instrumentsRepository.isClassificationNameAvailable,
		currentName: props.mode === "creation" ? undefined : defaultValues.name,
	});

	const { control, formState, watch, handleSubmit, reset } = useForm({
		defaultValues,
		resolver: zodResolver(
			useMemo(
				() => (mode === "creation" ? creationResolver(checkIfNameIsAvailable) : editResolver(checkIfNameIsAvailable)),
				[mode, checkIfNameIsAvailable],
			),
		),
	});

	props.setCanDismiss(!formState.isDirty);
	const observedClassification = watch();
	const allowedActions = useMemo(
		() => classificationAllowedAction(mode, observedClassification.standardClassificationId),
		[mode, observedClassification],
	);

	const queryStandardClassification = useQuery({
		...instrumentsRepository.standardClassifications.getQueryConfig(),
		enabled: props.mode === "creation",
	});

	// The premium-data licenses (fundDataAccessTier / bondDataAccessTier) are customer data, so
	// they come from the customer endpoint rather than /user/info. The options join both queries,
	// so the preset picker waits for the customer call too and the disabled + "(Premium data)"
	// state is known before the list renders — it never pops in after the fact.
	const user = useUserValue();
	const customerQuery = userRepository.customer.useQuery(user.customerId!);
	const standardClassificationOptionsQuery = useMappedQueries(
		([classifications, customer]): SelectOptions<StandardClassificationId> => [
			["standard", classifications.map((classification) => presetOptionFromClassification(classification, customer))],
		],
		[],
		[queryStandardClassification, customerQuery],
	);

	// Duration/YTM under exactly one of the two licenses: the option is enabled, and this banner
	// (shown only while such a preset is selected) says which instrument family the data covers.
	const selectedPresetCoverage = customerQuery.data
		? dualSourceCoverage(observedClassification.standardClassificationId, customerQuery.data)
		: undefined;

	const classificationMap = useMemo(
		() =>
			Immutable.Map<StandardClassificationId, StandardInstrumentsClassificationDto>(
				(queryStandardClassification.data ?? []).flatMap((x) =>
					x.standardClassificationId ? [[x.standardClassificationId, x]] : [],
				),
			),
		[queryStandardClassification.data],
	);

	return (
		<FormSidebarContent
			title={mode === "edit" ? (props.classification.name ?? "...") : "Add new column"}
			description={
				mode === "edit"
					? ""
					: "Customize your view by adding a new column. You can start with the available presets: the platform will automatically load the relevant tags and values, which you can still modify to suit your needs."
			}
			onCancel={onCancel}
			onSubmit={() =>
				handleSubmit(async (payload) => {
					if (mode === "creation") {
						await onSubmitAsync(payload);
						return;
					}

					await onSubmitAsync({
						...payload,
						classificationUuid: props.classification.classificationUuid,
					});
				}, console.error)()
			}
			footer={
				mode === "edit" && (
					<Button
						palette="tertiary"
						onClickAsync={async () => {
							const queryData = await instrumentsRepository.checkColumnInvestments.fetchQuery(
								props.classification.classificationUuid!,
							);
							const investmentsByOwner = Object.entries(queryData);

							spawnInstrumentCustomisationDeleteDialog({
								type: "delete-column",
								investmentsUsedIn: investmentsByOwner,
								onContinue: async () => {
									investmentsByOwner.length > 0
										? noop()
										: (async () => {
												instrumentsRepository.disableClassification(props.classification.classificationUuid!);
												onCancel();
											})();
								},
							});
						}}
					>
						<Icon icon="Close" />
						<Text type="Body/L/Medium" color="">
							Delete
						</Text>
					</Button>
				)
			}
		>
			{mode === "creation" && (
				<ReactQueryWrapperBase
					query={standardClassificationOptionsQuery}
					loadingFallback={<CircularProgressBar style={{ width: "1rem" }} value="indeterminate" />}
				>
					{(standardClassificationOptions) => (
						<FormFields.Select
							size="small"
							name="standardClassificationId"
							label={({ htmlFor }) => (
								<Row justifyContent="space-between" alignItems="center">
									<Label htmlFor={htmlFor}>Load from prefilled data</Label>
									<Text type="Body/S/Medium" color={themeCSSVars.palette_N500}>
										(optional)
									</Text>
								</Row>
							)}
							data-qualifier={qualifier.component.input.select("List/Preset")}
							triggerDataAttrs={{
								"data-qualifier": qualifier.component.input.select("Preset"),
							}}
							enableSearch
							control={control}
							formState={formState}
							options={standardClassificationOptions}
							unselectOnMatch
							onChange={(standardClassificationId: StandardClassificationId) => {
								if (standardClassificationId) {
									const presetClassification = classificationMap.get(standardClassificationId);
									if (observedClassification.standardClassificationId === standardClassificationId) {
										reset({ standardClassificationId: undefined });
										return;
									}

									if (presetClassification) {
										let colorIndex = 0;
										reset(
											{
												standardClassificationId,
												options: presetClassification.options?.flatMap((option) => {
													const healthyOption = option
														? [
																{
																	...option,
																	color: option.color ?? colors[colorIndex].key,
																},
															]
														: [];

													if (colorIndex === colors.length - 1) {
														colorIndex = 0;
														return healthyOption;
													}
													colorIndex += 1;
													return healthyOption;
												}),
												fieldType: presetClassification.fieldType,
												dataVisualization: DataVisualization.TagViewWithoutColor,
												name: presetClassification.name,
											},
											{ keepDefaultValues: true },
										);
									}
								}
							}}
							className="mb-4"
						/>
					)}
				</ReactQueryWrapperBase>
			)}
			{mode === "creation" && selectedPresetCoverage && (
				<Banner severity="info" className="mb-4" data-qualifier="InstrumentCustomisation/PresetCoverageBanner">
					{DUAL_SOURCE_COVERAGE_BANNER[selectedPresetCoverage]}
				</Banner>
			)}
			<FormFields.Text
				name="name"
				size="small"
				control={control}
				data-qualifier={qualifier.component.input.text("ColumnName")}
				label={({ htmlFor }) => (
					<Row alignItems="center" gap={8}>
						<Label htmlFor={htmlFor} className="mb-px">
							Column name
						</Label>

						<InfoTooltip>
							<TooltipContent>
								<Text type="Body/M/Medium" className="mb-2" as="p">
									Please follow these rules for valid name entry
								</Text>
								<ul style={{ listStyleType: "disc" }}>
									<ForEach
										collection={[
											"1-30 characters long",
											"Allowed: Letters, numbers, spaces, _ - [ ] ( ) {}",
											"No special characters like @, #, $, %",
										]}
									>
										{(item) => <li>{item}</li>}
									</ForEach>
								</ul>
							</TooltipContent>
						</InfoTooltip>
					</Row>
				)}
				formState={formState}
				className="mb-4"
				disabled={!allowedActions.canEditName}
				rightContent={
					checkingNameUniqueness ? <CircularProgressBar className="w-3" value="indeterminate" /> : undefined
				}
			/>
			<div className="flex gap-2 items-start mb-4">
				<FormFields.Select
					name="fieldType"
					control={control}
					label={({ htmlFor }) => (
						<Row alignItems="center" gap={8}>
							<Label htmlFor={htmlFor} className="mb-px">
								Column type
							</Label>

							<InfoTooltip>Choose the data format for the new column.</InfoTooltip>
						</Row>
					)}
					formState={formState}
					options={
						[
							{ value: "QUALITY", label: "Description" },
							{ value: "SCORE", label: "Number" },
							{ value: "TAG", label: "Tag" },
						] satisfies Array<SelectOption<InstrumentFieldType>>
					}
					data-qualifier={qualifier.component.input.select("List/ColumnType")}
					triggerDataAttrs={{
						"data-qualifier": qualifier.component.input.select("ColumnType"),
					}}
					className="flex-1"
					disabled={!allowedActions.canEditType}
					onChange={(newFieldType: InstrumentFieldType) => {
						reset(
							{
								fieldType: newFieldType,
								dataVisualization: newFieldType === "TAG" ? DataVisualization.TagViewWithoutColor : undefined,
							},
							{ keepDirtyValues: true },
						);
					}}
				/>
				{observedClassification.fieldType === "TAG" && (
					<FormFields.Select
						name="tagType"
						size="small"
						control={control}
						label={({ htmlFor }) => (
							<Row alignItems="center" gap={8}>
								<Label htmlFor={htmlFor} className="mb-px">
									Tag type
								</Label>

								<InfoTooltip>Define how this tag will interact with Portfolio Constraints.</InfoTooltip>
							</Row>
						)}
						data-qualifier={qualifier.component.input.select("List/TagType")}
						triggerDataAttrs={{
							"data-qualifier": qualifier.component.input.select("TagType"),
						}}
						formState={formState}
						disabled={!allowedActions.canEditTagType}
						options={
							[
								{ value: "ASSET_CLASS", label: "Asset Class" },
								{ value: "CURRENCY", label: "Currency" },
								{ value: "GENERAL", label: "General" },
							] satisfies Array<SelectOption<TagType>>
						}
						className="flex-1"
					/>
				)}
			</div>

			{observedClassification.fieldType === "TAG" && (
				<>
					<FormField
						label={({ htmlFor }) => (
							<Row alignItems="center" gap={8}>
								<Label htmlFor={htmlFor} className="mb-px">
									Column style
								</Label>

								<InfoTooltip>Select how the column data will be visually displayed in the table.</InfoTooltip>
							</Row>
						)}
					>
						{() => (
							<FormController
								control={control}
								disabled={!allowedActions.canEditVisualization}
								name="dataVisualization"
								data-qualifier={qualifier.component.input.select("TagType")}
								render={({ field: { value, onChange } }) => (
									<ButtonGroupRadio
										options={[
											{
												value: DataVisualization.BarChart,
												children: "Bar Chart",
												"data-qualifier": qualifier.component.radioGroupButton.option("BarChart"),
											},
											{
												value: DataVisualization.TagViewWithColor,
												children: "Color Tag",
												"data-qualifier": qualifier.component.radioGroupButton.option("ColorTag"),
											},
											{
												value: DataVisualization.TagViewWithoutColor,
												children: "Neutral Tag",
												"data-qualifier": qualifier.component.radioGroupButton.option("NeutralTag"),
											},
										]}
										onChange={onChange}
										value={value}
									/>
								)}
							/>
						)}
					</FormField>
					<hr className="my-4 -mx-4" />
					<InsertTagOptions
						classification={observedClassification}
						control={control}
						allowedActions={allowedActions}
						mode={mode}
					/>
				</>
			)}
		</FormSidebarContent>
	);
}

function InsertTagOptions(props: {
	control: Control<InstrumentClassificationForm, any>;
	classification: InstrumentClassificationForm;
	allowedActions: ClassificationAllowedAction;
	mode: InstrumentCustomizationSidePanelProps["mode"];
}) {
	const { control, allowedActions, classification, mode } = props;

	const optionsFieldArray = useFieldArray<InstrumentClassificationForm, "options", "id">({
		control,
		name: "options",
		keyName: "id",
	});

	const value = useMemo<TagOption[]>(
		() =>
			optionsFieldArray.fields.map((field) => ({
				option: field.option ?? "",
				color: field.color,
				canEdit: field.canEdit,
			})),
		[optionsFieldArray.fields],
	);

	return (
		<TagOptionsEditor
			value={value}
			onChange={(options) => optionsFieldArray.replace(options)}
			colorPalette={colors}
			allowSpecialCharacters
			allowAddOption={allowedActions.canAddOption}
			allowEditOptionOrder={allowedActions.canEditOptionOrder}
			allowEditOptionColor={allowedActions.canEditOptionColor}
			allowEditOptionName={allowedActions.canEditOptionName}
			onRenameOption={
				mode === "edit"
					? async (oldName, newName) => {
							await instrumentsRepository
								.renameTagClassification(classification.classificationUuid!, oldName, newName)
								.then(async () => {
									await typedQueryClient().invalidateQueries("userInstrumentsBaseQueryKey");
									await typedQueryClient().refetchQueries("userInstrumentsBaseQueryKey");
								})
								.catch(noop);
						}
					: undefined
			}
			allowRemoveOption={allowedActions.canRemoveOption}
			onRemoveOptionAsync={
				mode === "edit"
					? async (option, index) => {
							const userInvestmentMap = await instrumentsRepository.verifyTagClassificationUsage.fetchQuery(
								classification.classificationUuid!,
								option.option,
							);

							const investmentsUsedIn = typedObjectEntries(userInvestmentMap);

							spawnInstrumentCustomisationDeleteDialog({
								type: "delete-tag",
								investmentsUsedIn,
								async onContinue() {
									if (investmentsUsedIn.length > 0) {
										return noop();
									}
									await instrumentsRepository.deleteTagClassification(
										classification.classificationUuid!,
										option.option,
									);
									await typedQueryClient().invalidateQueries("userInstrumentsBaseQueryKey");
									await typedQueryClient().refetchQueries("userInstrumentsBaseQueryKey");
									optionsFieldArray.remove(index);
								},
							});
						}
					: undefined
			}
			qualifiers={{
				addInput: qualifier.component.input.text("SearchOptionToAdd"),
				addButton: qualifier.component.button("AddTag"),
				renameInput: qualifier.component.input.text("RenameOption"),
				deleteButton: (index, option) => qualifier.component.button(`Delete/item(${index}, ${option})`),
			}}
		/>
	);
}

type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;
type SpawnInstrumentCustomizationSidePanelProps = DistributiveOmit<
	InstrumentCustomizationSidePanelProps,
	"onCancel" | "setCanDismiss"
>;
export function spawnInstrumentCustomizationSidePanel(
	params: {
		signal?: AbortSignal;
	} & SpawnInstrumentCustomizationSidePanelProps,
): Promise<void> {
	return spawnSidePanel({
		signal: params.signal,
		children(props) {
			return match(params)
				.with({ mode: "edit" }, (castParams) => (
					<InstrumentCustomizationSidePanel
						mode={castParams.mode}
						classification={castParams.classification}
						onSubmitAsync={async (classification) => {
							await castParams.onSubmitAsync(classification);
							props.resolve();
						}}
						onCancel={props.reject}
						setCanDismiss={props.setCanDismiss}
					/>
				))
				.with({ mode: "creation" }, (castParams) => (
					<InstrumentCustomizationSidePanel
						mode={castParams.mode}
						onSubmitAsync={async (classification) => {
							await castParams.onSubmitAsync(classification);
							props.resolve();
						}}
						onCancel={props.reject}
						setCanDismiss={props.setCanDismiss}
					/>
				))
				.exhaustive();
		},
	});
}

export default InstrumentCustomizationSidePanel;
